import { useEffect, useState, useCallback, useMemo, Component, type ReactNode } from "react";
import { phCapture } from "@/lib/posthog";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, Download, PlusCircle, ExternalLink, Copy, Check, Info, MessageSquare, AlertTriangle, CheckCircle2, XCircle, ClipboardList, ChevronDown, ChevronRight, Trash2, Lock, HelpCircle, Loader2, RefreshCw } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Layout from "@/components/Layout";
import RequireAuth from "@/components/RequireAuth";
import { generateReportPdf } from "@/lib/generatePdf";
import CopyButton from "@/components/CopyButton";
import ReportNavigator, { TocSection } from "@/components/ReportNavigator";
import { copyToClipboard, normalizeCopyText } from "@/lib/copy";
import { buildSalesPage, buildSalesPageText } from "@/lib/buildSalesPage";
import { improveListing, buildImprovementText, type ListingImprovementOutput } from "@/lib/improveListing";
import { Textarea } from "@/components/ui/textarea";
import { useUserPlan } from "@/hooks/useUserPlan";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Sentry } from "@/lib/sentry";

// ── Error Boundary ──
class ReportErrorBoundary extends Component<
  { reportId?: string; children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { reportId?: string; children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: { reportId: this.props.reportId ?? "unknown" },
      contexts: { react: { componentStack: info.componentStack ?? undefined } },
    });
  }
  render() {
    if (this.state.hasError) {
      return (
        <Layout>
          <div className="dark-page flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
            <AlertTriangle className="h-10 w-10 text-destructive" />
            <h2 className="text-xl font-bold text-white">Something went wrong displaying this report</h2>
            <p className="text-sm text-slate-400 max-w-md">{this.state.error?.message}</p>
            {this.props.reportId && (
              <p className="text-xs text-slate-500 font-mono">Report ID: {this.props.reportId}</p>
            )}
            <div className="flex gap-3">
              <Button variant="outline" className="border-white/10 text-slate-300" onClick={() => window.location.reload()}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Retry
              </Button>
              <Link to="/history">
                <Button variant="outline" className="border-white/10 text-slate-300">
                  <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back to History
                </Button>
              </Link>
            </div>
          </div>
        </Layout>
      );
    }
    return this.props.children;
  }
}

// ── Loading Skeleton ──
const ReportSkeleton = () => (
  <Layout>
    <div className="dark-page">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-8 space-y-6">
        <div className="space-y-3">
          <Skeleton className="h-10 w-3/4 bg-white/5" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-24 bg-white/5" />
            <Skeleton className="h-6 w-32 bg-white/5" />
          </div>
          <Skeleton className="h-4 w-40 bg-white/5" />
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} className="card-surface p-6 space-y-3">
            <Skeleton className="h-6 w-1/3 bg-white/5" />
            <Skeleton className="h-4 w-full bg-white/5" />
            <Skeleton className="h-4 w-5/6 bg-white/5" />
            <Skeleton className="h-4 w-2/3 bg-white/5" />
          </div>
        ))}
      </div>
    </div>
  </Layout>
);

const ADMIN_EMAILS = ["admin@scopsift.com"];

// Helper: section header with copy button + collapsible chevron
const SectionHeader = ({ title, getText, className, isOpen, onToggle }: { title: string; getText: () => string; className?: string; isOpen?: boolean; onToggle?: () => void }) => (
  <div className={`flex items-center justify-between gap-2 ${className || ""}`}>
    <div className="flex items-center gap-2 cursor-pointer" onClick={onToggle}>
      {onToggle && (
        isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
      )}
      <h3 className="text-xl font-bold text-white">{title}</h3>
    </div>
    <CopyButton getText={getText} size="icon" variant="ghost" />
  </div>
);

// Collapsible card wrapper
const CollapsibleCard = ({ id, isOpen, onToggle, searchHighlight, children }: {
  id: string;
  isOpen: boolean;
  onToggle: () => void;
  searchHighlight?: boolean;
  children: React.ReactNode;
}) => (
  <div
    id={id}
    className={`card-surface transition-all ${searchHighlight ? "ring-1 ring-teal-400/50" : ""}`}
  >
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      {children}
    </Collapsible>
  </div>
);

const Report = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const { toast } = useToast();
  const { plan } = useUserPlan();
  const { user } = useAuth();
  const isFree = plan === "free";
  const isAdmin = ADMIN_EMAILS.includes(user?.email || "");

  const handleDeleteReport = async () => {
    const { error } = await supabase.from("reports").delete().eq("id", id);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Report deleted" });
    navigate("/history");
  };

  // Navigator state
  const [activeSectionId, setActiveSectionId] = useState("summary");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(["summary", "copy-pack"]));
  const [searchQuery, setSearchQuery] = useState("");
  const [listingInput, setListingInput] = useState("");
  const [listingResult, setListingResult] = useState<ListingImprovementOutput | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSections(new Set(["summary", "market-radar", "price-radar", "themes", "positioning", "seller-claims", "desired-outcomes", "objections", "copy-pack", "sales-page", "improve-listing", "sources", "key-pains", "top-objections", "reassurance-angles", "message-pillars", "hooks", "ad-angles", "faq", "do-dont-claims"]));
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  const handleNavigate = useCallback((sectionId: string) => {
    // Expand the section first
    setExpandedSections(prev => new Set([...prev, sectionId]));
    // Small delay to allow collapsible to open before scrolling
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, []);

  // Scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // IntersectionObserver for active section
  useEffect(() => {
    if (loading || !report) return;
    const sectionIds = ["summary", "market-radar", "price-radar", "themes", "positioning", "seller-claims", "desired-outcomes", "objections", "copy-pack", "sales-page", "improve-listing", "sources", "key-pains", "top-objections", "reassurance-angles", "message-pillars", "hooks", "ad-angles", "faq", "do-dont-claims"];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
            break;
          }
        }
      },
      { threshold: 0.15, rootMargin: "-80px 0px -60% 0px" }
    );
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading, report]);

  const extractAsin = (url: string): string | null => {
    const match = url.match(/(?:\/dp\/|\/gp\/product\/)([A-Z0-9]{10})/i);
    return match ? match[1] : null;
  };

  const copyLink = (url: string, idx: number) => {
    navigator.clipboard.writeText(url);
    setCopiedIdx(idx);
    toast({ title: "Link copied" });
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  // ── Polling fetch logic ──
  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const startTime = Date.now();
    const MAX_POLL_MS = 60_000;

    const fetchReport = async () => {
      try {
        const { data, error } = await supabase.from("reports").select("*").eq("id", id).single();
        if (cancelled) return;

        if (error) {
          // Real error (not just missing)
          setFetchError(error.message);
          setLoading(false);
          return;
        }

        setReport(data);
        setFetchError(null);

        const isReady = data && data.status === "complete" && data.copy_pack;
        const isProcessing = data && (data.status === "pending" || data.status === "processing" || !data.copy_pack);

        if (isReady) {
          setLoading(false);
          phCapture("report_viewed", { report_id: id, plan });
          return;
        }

        if (isProcessing) {
          const elapsed = Date.now() - startTime;
          if (elapsed >= MAX_POLL_MS) {
            setPollTimedOut(true);
            setLoading(false);
            return;
          }
          attempt++;
          const delay = attempt <= 13 ? 1500 : 3000; // 1.5s for ~20s, then 3s
          setTimeout(fetchReport, delay);
          return;
        }

        // Fallback: data exists but unknown status
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        Sentry.captureException(err, { tags: { reportId: id ?? "unknown" } });
        setFetchError(err?.message || "Failed to load report");
        setLoading(false);
      }
    };

    fetchReport();
    return () => { cancelled = true; };
  }, [id]);

  const handleRetryPoll = () => {
    setLoading(true);
    setFetchError(null);
    setPollTimedOut(false);
    // Re-trigger by forcing a re-mount of the effect via a dummy state isn't ideal,
    // so we'll just reload. The effect depends on [id] which won't change.
    window.location.reload();
  };

  // ── UI state guards ──
  if (loading) {
    return <RequireAuth><ReportSkeleton /></RequireAuth>;
  }

  if (fetchError && !report) {
    return (
      <RequireAuth><Layout>
        <div className="dark-page flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <h2 className="text-xl font-bold text-white">Failed to load report</h2>
          <p className="text-sm text-slate-400 max-w-md">{fetchError}</p>
          {isAdmin && <p className="text-xs text-slate-500 font-mono">ID: {id}</p>}
          <div className="flex gap-3">
            <Button onClick={handleRetryPoll} className="btn-gradient gap-1.5" size="sm">
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </Button>
            <Link to="/history">
              <Button variant="outline" size="sm" className="border-white/10 text-slate-300">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back to History
              </Button>
            </Link>
          </div>
        </div>
      </Layout></RequireAuth>
    );
  }

  if (!report) {
    return (
      <RequireAuth><Layout>
        <div className="dark-page flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
          <XCircle className="h-10 w-10 text-slate-500" />
          <h2 className="text-xl font-bold text-white">Report not found</h2>
          <p className="text-sm text-slate-400">This report may have been deleted or the link is invalid.</p>
          <Link to="/history">
            <Button variant="outline" size="sm" className="border-white/10 text-slate-300">
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back to History
            </Button>
          </Link>
        </div>
      </Layout></RequireAuth>
    );
  }

  // Report exists but still processing (either polling timed out or still pending)
  if (report.status === "pending" || report.status === "processing" || (!report.copy_pack && report.status !== "complete")) {
    return (
      <RequireAuth><Layout>
        <div className="dark-page flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
          {pollTimedOut ? (
            <>
              <AlertTriangle className="h-10 w-10 text-amber-400" />
              <h2 className="text-xl font-bold text-white">Your report is taking longer than usual</h2>
              <p className="text-sm text-slate-400 max-w-md">Generation is still in progress. Tap to retry or check back shortly.</p>
              <div className="flex gap-3">
                <Button onClick={handleRetryPoll} className="btn-gradient gap-1.5" size="sm">
                  <RefreshCw className="h-3.5 w-3.5" /> Retry
                </Button>
                <Link to={`/processing/${id}`}>
                  <Button variant="outline" size="sm" className="border-white/10 text-slate-300">View Progress</Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="h-10 w-10 text-teal-400 animate-spin" />
              <h2 className="text-xl font-bold text-white">Finalising your report…</h2>
              <p className="text-sm text-slate-400">This usually takes a few seconds. Hang tight.</p>
            </>
          )}
          {isAdmin && (
            <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/10 text-left text-xs text-slate-500 font-mono max-w-sm w-full">
              <p>Report ID: {id}</p>
              <p>Status: {report.status}</p>
              <p>copy_pack: {report.copy_pack ? "present" : "null"}</p>
              {fetchError && <p>Last error: {fetchError}</p>}
            </div>
          )}
        </div>
      </Layout></RequireAuth>
    );
  }

  const themes = (report.themes as any[]) || [];
  const outcomes = (report.desired_outcomes as string[]) || [];
  const objections = (report.objections as any[]) || [];
  const pack = report.copy_pack as any;
  const sources = (report.sources as string[]) || [];
  const reviewEvidenceAvailable = report.review_evidence_available !== false;
  const sellerClaims = (report.seller_claims_examples as string[]) || [];
  const evidenceSourcesUsed = (report.evidence_sources_used as any[]) || [];
  const templateType = report.template_type || "Amazon listing copy pack";
  const isGenericTemplate = templateType === "Generic marketing messaging pack";

  const themeType = pack?.theme_type || "pain_themes";
  const themeHeading = themeType === "positioning_themes"
    ? `Top ${themes.length} Positioning Themes`
    : themeType === "market_themes"
    ? `Top ${themes.length} Market Themes`
    : `Top ${themes.length} Pain Themes`;

  const hasWebEvidence = evidenceSourcesUsed.some((s: any) => ["reddit", "youtube", "web", "competitor_website"].includes(s.type));
  const hasYoutubeWithoutComments = evidenceSourcesUsed.some((s: any) => s.type === "youtube" && s.note);

  const evidenceSectionTitle = hasWebEvidence && reviewEvidenceAvailable
    ? "Real user evidence (web)"
    : "Sources";

  const getSourceLabel = (url: string) => {
    const asin = extractAsin(url);
    if (asin) return `Amazon UK • ASIN: ${asin}`;
    try {
      const hostname = new URL(url).hostname.replace("www.", "");
      if (hostname.includes("reddit.com")) return `Reddit`;
      if (hostname.includes("youtube.com")) return `YouTube`;
      return hostname;
    } catch { return url; }
  };

  const getSourceType = (url: string): { type?: string; category?: string } => {
    const match = evidenceSourcesUsed.find((s: any) => s.url === url);
    return { type: match?.type, category: match?.category };
  };

  // ── "Copy everything" builder ──
  const buildFullText = (): string => {
    const lines: string[] = [];
    lines.push("ScopSift Report");
    lines.push(`Title: ${report.topic || report.product_category}`);
    if (report.industry_context) lines.push(`Industry: ${report.industry_context}`);
    lines.push(`Generated: ${new Date(report.created_at).toLocaleDateString()}`);
    lines.push("");

    if (pack?.summary) {
      lines.push("SUMMARY");
      lines.push(pack.summary);
      lines.push("");
    }

    if (report.market_radar_enabled && report.delta_summary) {
      lines.push("MARKET RADAR");
      lines.push(report.delta_summary);
      if (report.baseline_report_id && report.delta) {
        const delta = report.delta as any;
        for (const [label, key] of [
          ["New pain themes", "new_pain_themes"],
          ["Rising pain themes", "rising_pain_themes"],
          ["Falling pain themes", "falling_pain_themes"],
          ["New objections", "new_objections"],
          ["New language buyers use", "new_language_buyers_use"],
          ["New competitor claims", "new_competitor_claims"],
        ]) {
          const items = delta?.[key] as string[] | undefined;
          if (items?.length) {
            lines.push(`${label}:`);
            items.forEach(item => lines.push(`- ${item}`));
          }
        }
      }
      lines.push("");
    }

    const priceRadar = report.price_radar as any;
    if (report.price_radar_enabled && priceRadar) {
      lines.push("RETAIL PRICE RADAR (WEB SNAPSHOT)");
      if (priceRadar.price_range) lines.push(`Range: ${priceRadar.price_range.currency}${priceRadar.price_range.min} – ${priceRadar.price_range.currency}${priceRadar.price_range.max}`);
      if (priceRadar.common_band) lines.push(`Most common band: ${priceRadar.common_band}`);
      if (priceRadar.median_price) lines.push(`Median: ~${priceRadar.price_range?.currency || "£"}${priceRadar.median_price}`);
      if (priceRadar.low_vs_high) lines.push(`\nLow vs high: ${priceRadar.low_vs_high}`);
      if (priceRadar.pricing_signals?.length) {
        lines.push("\nPricing signals:");
        priceRadar.pricing_signals.forEach((s: string) => lines.push(`- ${s}`));
      }
      if (priceRadar.source_urls?.length) {
        lines.push("\nSources:");
        priceRadar.source_urls.forEach((u: string) => lines.push(`- ${u}`));
      }
      lines.push(`\n${priceRadar.disclaimer || ""}`);
      lines.push("");
    }

    if (isGenericTemplate && pack) {
      if (pack.positioning_statement) { lines.push("POSITIONING"); lines.push(pack.positioning_statement); lines.push(""); }
      if (pack.key_pains?.length) { lines.push("KEY PAINS"); pack.key_pains.forEach((p: string) => lines.push(`- ${p}`)); lines.push(""); }
      if (pack.top_objections?.length) { lines.push("TOP OBJECTIONS"); pack.top_objections.forEach((o: any) => { lines.push(`Objection: ${o.objection}`); lines.push(`Rebuttal: ${o.rebuttal}`); lines.push(""); }); }
      if (pack.reassurance_angles?.length) { lines.push("REASSURANCE ANGLES"); pack.reassurance_angles.forEach((r: string) => lines.push(`- ${r}`)); lines.push(""); }
      if (pack.message_pillars?.length) { lines.push("MESSAGE PILLARS"); pack.message_pillars.forEach((m: string) => lines.push(`- ${m}`)); lines.push(""); }
      if (pack.hooks?.length) { lines.push("HOOKS"); pack.hooks.forEach((h: string) => lines.push(`- ${h}`)); lines.push(""); }
      if (pack.ad_angles?.length) { lines.push("AD ANGLES"); pack.ad_angles.forEach((a: any) => { lines.push(`${a.angle_name}: ${a.description}`); }); lines.push(""); }
      if (pack.faqs?.length) { lines.push("FAQ"); pack.faqs.forEach((f: any) => { lines.push(`Q: ${f.q}`); lines.push(`A: ${f.a}`); lines.push(""); }); }
      if (pack.do_dont_claims) {
        lines.push("DO / DON'T CLAIMS");
        if (pack.do_dont_claims.do?.length) { lines.push("Safe to claim:"); pack.do_dont_claims.do.forEach((c: string) => lines.push(`✓ ${c}`)); }
        if (pack.do_dont_claims.dont?.length) { lines.push("Avoid claiming:"); pack.do_dont_claims.dont.forEach((c: string) => lines.push(`✗ ${c}`)); }
        lines.push("");
      }
    } else {
      if (themes.length) {
        lines.push(themeHeading.toUpperCase());
        themes.forEach((t: any, i: number) => {
          lines.push(`${i + 1}. ${t.title}`);
          t.bullets?.forEach((b: string) => lines.push(`  - ${b}`));
          if (t.messaging_angle) lines.push(`  Messaging angle: ${t.messaging_angle}`);
        });
        lines.push("");
      }

      if (sellerClaims.length) {
        lines.push("SELLER CLAIMS");
        sellerClaims.forEach(c => lines.push(`- ${c}`));
        lines.push("");
      }

      if (outcomes.length) {
        lines.push("DESIRED OUTCOMES");
        outcomes.forEach(o => lines.push(`- ${o}`));
        lines.push("");
      }

      if (objections.length) {
        lines.push("OBJECTIONS & REASSURANCE");
        objections.forEach((o: any) => { lines.push(`Objection: ${o.objection}`); lines.push(`Reassurance: ${o.reassurance}`); lines.push(""); });
      }

      if (pack) {
        lines.push("COPY PACK");
        if (pack.titles?.length) { lines.push("Titles:"); pack.titles.forEach((t: string, i: number) => lines.push(`${i+1}. ${t}`)); lines.push(""); }
        if (pack.bullets?.length) { lines.push("Bullets:"); pack.bullets.forEach((b: string) => lines.push(`- ${b}`)); lines.push(""); }
        if (pack.description) { lines.push("Description:"); lines.push(pack.description); lines.push(""); }
        if (pack.faqs?.length) { lines.push("FAQs:"); pack.faqs.forEach((f: any) => { lines.push(`Q: ${f.q}`); lines.push(`A: ${f.a}`); lines.push(""); }); }
        if (pack.ad_hooks?.length) { lines.push("Ad Hooks:"); pack.ad_hooks.forEach((h: string) => lines.push(`- ${h}`)); lines.push(""); }
      }
    }

    // Sales Page Builder
    if (pack || themes.length > 0) {
      const sp = buildSalesPage(report, themes, objections, outcomes, sellerClaims, pack);
      lines.push(buildSalesPageText(sp));
      lines.push("");
    }

    if (sources.length) {
      lines.push("SOURCES");
      sources.forEach(s => lines.push(`- ${s}`));
    }

    return lines.join("\n");
  };

  const buildCopyPackText = (): string => {
    if (!pack) return "";
    const parts: string[] = [];
    if (pack.titles?.length) { parts.push("Titles:"); pack.titles.forEach((t: string, i: number) => parts.push(`${i+1}. ${t}`)); parts.push(""); }
    if (pack.bullets?.length) { parts.push("Bullets:"); pack.bullets.forEach((b: string) => parts.push(`- ${b}`)); parts.push(""); }
    if (pack.description) { parts.push("Description:"); parts.push(pack.description); parts.push(""); }
    if (pack.faqs?.length) { parts.push("FAQs:"); pack.faqs.forEach((f: any) => { parts.push(`Q: ${f.q}`); parts.push(`A: ${f.a}`); parts.push(""); }); }
    if (pack.ad_hooks?.length) { parts.push("Ad Hooks:"); pack.ad_hooks.forEach((h: string) => parts.push(`- ${h}`)); }
    return parts.join("\n");
  };

  const buildObjectionsText = (): string => {
    return objections.map((o: any) => `Objection: ${o.objection}\nReassurance: ${o.reassurance}`).join("\n\n");
  };

  const buildPriceRadarText = (): string => {
    const pr = report.price_radar as any;
    if (!pr) return "";
    const parts: string[] = [];
    if (pr.price_range) parts.push(`Range: ${pr.price_range.currency}${pr.price_range.min} – ${pr.price_range.currency}${pr.price_range.max}`);
    if (pr.common_band) parts.push(`Most common band: ${pr.common_band}`);
    if (pr.median_price) parts.push(`Median: ~${pr.price_range?.currency || "£"}${pr.median_price}`);
    if (pr.low_vs_high) parts.push(`Low vs high: ${pr.low_vs_high}`);
    return parts.join("\n");
  };

  const themeText = (t: any, i: number) => {
    const parts = [`${i + 1}. ${t.title}`];
    t.bullets?.forEach((b: string) => parts.push(`- ${b}`));
    if (t.messaging_angle) parts.push(`Messaging angle: ${t.messaging_angle}`);
    return parts.join("\n");
  };

  const isHighlighted = (label: string) => searchQuery && label.toLowerCase().includes(searchQuery.toLowerCase());

  // Build TOC sections
  const tocSections: TocSection[] = isGenericTemplate
    ? [
        { id: "summary", label: "Summary", present: !!pack?.summary },
        { id: "market-radar", label: "Market Radar", present: !!(report.market_radar_enabled && report.delta) },
        { id: "price-radar", label: "Price Radar", present: !!(report.price_radar_enabled && report.price_radar) },
        { id: "positioning", label: "Positioning", present: !!pack?.positioning_statement },
        { id: "key-pains", label: "Key Pains", present: !!(pack?.key_pains?.length > 0) },
        { id: "top-objections", label: "Top Objections", present: !!(pack?.top_objections?.length > 0) },
        { id: "reassurance-angles", label: "Reassurance Angles", present: !!(pack?.reassurance_angles?.length > 0) },
        { id: "message-pillars", label: "Message Pillars", present: !!(pack?.message_pillars?.length > 0) },
        { id: "hooks", label: "Hooks", present: !!(pack?.hooks?.length > 0) },
        { id: "ad-angles", label: "Ad Angles", present: !!(pack?.ad_angles?.length > 0) },
        { id: "faq", label: "FAQ", present: !!(pack?.faqs?.length > 0) },
        { id: "do-dont-claims", label: "Do / Don't Claims", present: !!pack?.do_dont_claims },
        { id: "sources", label: "Sources", present: sources.length > 0 },
      ]
    : [
        { id: "summary", label: "Summary", present: true },
        { id: "market-radar", label: "Market Radar", present: !!(report.market_radar_enabled && report.delta) },
        { id: "price-radar", label: "Price Radar", present: !!(report.price_radar_enabled && report.price_radar) },
        { id: "themes", label: themeHeading, present: themes.length > 0 },
        { id: "seller-claims", label: "Seller Claims", present: sellerClaims.length > 0 },
        { id: "desired-outcomes", label: "Desired Outcomes", present: outcomes.length > 0 },
        { id: "objections", label: "Objections", present: objections.length > 0 },
        { id: "copy-pack", label: "Copy Pack", present: !!pack },
        { id: "sales-page", label: "Sales Page Builder", present: !!(pack || themes.length > 0) },
        { id: "improve-listing", label: "Improve My Listing", present: true },
        { id: "sources", label: "Sources", present: sources.length > 0 },
      ];

  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      await generateReportPdf({
        product_category: report.product_category,
        marketplace: report.marketplace,
        created_at: report.created_at,
        themes,
        desired_outcomes: outcomes,
        objections,
        copy_pack: pack,
        sources,
        review_evidence_available: reviewEvidenceAvailable,
        seller_claims_examples: sellerClaims,
        evidence_sources_used: evidenceSourcesUsed,
        template_type: templateType,
        topic: report.topic,
        industry_context: report.industry_context,
        market_radar_enabled: report.market_radar_enabled,
        baseline_report_id: report.baseline_report_id,
        delta_summary: report.delta_summary,
        delta: report.delta,
        price_radar_enabled: report.price_radar_enabled,
        price_radar: report.price_radar,
      });
      supabase.from("reports").update({ pdf_url: "generated-client-side" }).eq("id", id).then(() => {});
    } catch (e: any) {
      toast({ title: "PDF generation failed", description: e?.message || "Try again", variant: "destructive" });
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <RequireAuth><ReportErrorBoundary reportId={id}><Layout>
      <div className="dark-page">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8 space-y-6">
          {/* ── Report Header ── */}
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight text-white">
                {report.topic || (isGenericTemplate ? "Marketing Messaging Pack" : "Conversion Copy Pack")}
              </h1>
              <div className="flex flex-wrap gap-2 mt-3">
                {report.product_category && report.product_category !== "Other" && (
                  <Badge className="bg-teal-500/15 text-teal-300 border-0">{report.product_category}</Badge>
                )}
                <Badge className="border-white/10 text-slate-400 bg-transparent">{templateType}</Badge>
              </div>
              <p className="mt-2 text-xs text-slate-400">Generated {new Date(report.created_at).toLocaleDateString()}</p>
            </div>

            {/* Action Bar (header) */}
            <div className="flex items-center gap-2 shrink-0">
              <Button className="btn-gradient gap-1.5" size="sm" onClick={handleDownloadPdf} disabled={generatingPdf}>
                {generatingPdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {generatingPdf ? "Generating…" : "Download PDF"}
              </Button>
              <CopyButton getText={buildFullText} label="Copy everything" />
              <Button
                variant="outline"
                size="sm"
                className="border-white/10 text-slate-400 hover:text-white"
                onClick={() => window.open("https://forms.gle/7JHiUT56vXBUQuTb9", "_blank")}
              >
                <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Feedback
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0f1629] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Delete this report?</AlertDialogTitle>
                    <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-white/10 text-slate-300">Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteReport}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* ── Sticky scroll progress bar ── */}
          <div className="sticky top-14 z-50 -mx-4 md:-mx-6">
            <div className="h-0.5 bg-white/5">
              <div
                className="h-full bg-gradient-to-r from-teal-400 to-sky-400 transition-all duration-150"
                style={{ width: `${scrollProgress}%` }}
              />
            </div>
          </div>

          {/* ── Sticky Quick Actions ── */}
          <div className="sticky top-[calc(3.5rem+2px)] z-40 -mx-4 md:-mx-6 px-4 md:px-6 py-2 bg-[#0a0e1a]/95 backdrop-blur-sm border-b border-white/5">
            <div className="flex items-center gap-2 flex-wrap">
              <Button className="btn-gradient gap-1.5 h-7 text-xs" size="sm" onClick={handleDownloadPdf} disabled={generatingPdf}>
                {generatingPdf ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                {generatingPdf ? "PDF…" : "PDF"}
              </Button>
              <CopyButton getText={buildFullText} label="Copy all" />
              {pack && <CopyButton getText={buildCopyPackText} label="Copy Pack" />}
              {objections.length > 0 && <CopyButton getText={buildObjectionsText} label="Objections" />}
              {report.price_radar_enabled && report.price_radar && <CopyButton getText={buildPriceRadarText} label="Price Radar" />}
              <div className="ml-auto flex gap-1">
                <Button variant="ghost" size="sm" className="h-7 text-[11px] text-slate-200 hover:text-white" onClick={expandAll}>
                  Expand all
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-[11px] text-slate-200 hover:text-white" onClick={collapseAll}>
                  Collapse all
                </Button>
              </div>
            </div>
          </div>

          {/* ── Two-column layout (TOC left, content right) ── */}
          <div className="flex gap-8">
            {/* Desktop Navigator Sidebar (LEFT) */}
            <ReportNavigator
              sections={tocSections}
              activeId={activeSectionId}
              onNavigate={handleNavigate}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              scrollProgress={scrollProgress}
            />
            {/* Main content */}
            <div className="flex-1 min-w-0 space-y-6">

              {/* Summary */}
              <CollapsibleCard id="summary" isOpen={expandedSections.has("summary")} onToggle={() => toggleSection("summary")} searchHighlight={isHighlighted("Summary")}>
                <SectionHeader title="Summary" getText={() => pack?.summary || `Analysis of ${sources.length} sources in the "${report.product_category}" category.`} isOpen={expandedSections.has("summary")} onToggle={() => toggleSection("summary")} />
                <CollapsibleContent>
                  <p className="mt-4 text-sm leading-7 text-slate-200/90">
                    {pack?.summary || `Analysis of ${sources.length} sources in the "${report.product_category}" category.`}
                  </p>
                </CollapsibleContent>
              </CollapsibleCard>

              {/* Evidence used */}
              {report.status === "complete" && (
                <div className="space-y-1">
                  {(report.topic || report.industry_context) && (
                    <p className="text-xs text-slate-400 italic">
                      {report.topic && <>Topic: {report.topic}</>}
                      {report.topic && report.industry_context && <> · </>}
                      {report.industry_context && <>Industry/context: {report.industry_context}</>}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 italic">
                    Evidence used: {(() => {
                      const hasPasted = evidenceSourcesUsed.some((s: any) => s.type === "amazon_pasted");
                      const parts: string[] = [];
                      if (hasPasted) parts.push("Amazon low-star reviews (user-pasted)");
                      if (hasWebEvidence) parts.push("Web chatter");
                      return parts.length > 0 ? parts.join(" + ") : "Web chatter only";
                    })()}
                  </p>
                </div>
              )}

              {/* ─── Market Radar Section ─── */}
              {report.market_radar_enabled && report.delta && (
                <CollapsibleCard id="market-radar" isOpen={expandedSections.has("market-radar")} onToggle={() => toggleSection("market-radar")} searchHighlight={isHighlighted("Market Radar")}>
                  <SectionHeader
                    title="Market Radar: What changed since last run"
                    isOpen={expandedSections.has("market-radar")}
                    onToggle={() => toggleSection("market-radar")}
                    getText={() => {
                      const parts = [report.delta_summary || ""];
                      if (report.baseline_report_id && report.delta) {
                        const delta = report.delta as any;
                        for (const [label, key] of [["New pain themes","new_pain_themes"],["Rising","rising_pain_themes"],["Falling","falling_pain_themes"],["New objections","new_objections"],["New language","new_language_buyers_use"],["New competitor claims","new_competitor_claims"]]) {
                          const items = delta?.[key] as string[] | undefined;
                          if (items?.length) { parts.push(`${label}:`); items.forEach(item => parts.push(`- ${item}`)); }
                        }
                      }
                      return parts.join("\n");
                    }}
                  />
                  <CollapsibleContent>
                    <div className="mt-4 space-y-4">
                      {report.delta_summary && (
                        <p className="text-sm leading-7 text-slate-200/90">
                          {report.delta_summary}
                          {!report.baseline_report_id && (
                            <span className="block mt-2 text-xs italic text-slate-400">Tip: run again tomorrow or next week to see what changed.</span>
                          )}
                        </p>
                      )}
                      {report.baseline_report_id && (() => {
                        const delta = report.delta as any;
                        const sections: { label: string; key: string }[] = [
                          { label: "New pain themes", key: "new_pain_themes" },
                          { label: "Rising pain themes", key: "rising_pain_themes" },
                          { label: "Falling pain themes", key: "falling_pain_themes" },
                          { label: "New objections", key: "new_objections" },
                          { label: "New language buyers use", key: "new_language_buyers_use" },
                          { label: "New competitor claims", key: "new_competitor_claims" },
                        ];
                        return sections.map(({ label, key }) => {
                          const items = delta?.[key] as string[] | undefined;
                          if (!items?.length) return null;
                          return (
                            <div key={key}>
                              <h4 className="text-sm font-semibold text-white mb-1">{label}</h4>
                              <ul className="list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-0.5">
                                {items.map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    <p className="mt-4 text-xs text-teal-400/80 italic border-t border-white/5 pt-3">
                      Next action: update your listing/ad copy using the New + Rising themes, then re-run weekly to stay current.
                    </p>
                  </CollapsibleContent>
                </CollapsibleCard>
              )}

              {/* ─── Retail Price Radar ─── */}
              {report.price_radar_enabled && report.price_radar && (() => {
                const pr = report.price_radar as any;
                return (
                  <CollapsibleCard id="price-radar" isOpen={expandedSections.has("price-radar")} onToggle={() => toggleSection("price-radar")} searchHighlight={isHighlighted("Price Radar")}>
                    <SectionHeader title="Retail Price Radar (web snapshot)" isOpen={expandedSections.has("price-radar")} onToggle={() => toggleSection("price-radar")} getText={() => {
                      const parts: string[] = [];
                      if (pr.price_range) parts.push(`Range: ${pr.price_range.currency}${pr.price_range.min} – ${pr.price_range.currency}${pr.price_range.max}`);
                      if (pr.common_band) parts.push(`Most common band: ${pr.common_band}`);
                      if (pr.median_price) parts.push(`Median: ~${pr.price_range?.currency || "£"}${pr.median_price}`);
                      if (pr.low_vs_high) parts.push(`\nLow vs high: ${pr.low_vs_high}`);
                      if (pr.pricing_signals?.length) { parts.push("\nPricing signals:"); pr.pricing_signals.forEach((s: string) => parts.push(`- ${s}`)); }
                      if (pr.source_urls?.length) { parts.push("\nSources:"); pr.source_urls.forEach((u: string) => parts.push(`- ${u}`)); }
                      parts.push(`\n${pr.disclaimer || ""}`);
                      return parts.join("\n");
                    }} />
                    <CollapsibleContent>
                      <div className="mt-4 space-y-4">
                        {pr.price_range && (
                          <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-xl dark-surface-alt border p-3 text-center">
                              <p className="text-xs text-slate-400 mb-1">Range</p>
                              <p className="text-sm font-semibold text-white">{pr.price_range.currency}{pr.price_range.min} – {pr.price_range.currency}{pr.price_range.max}</p>
                            </div>
                            <div className="rounded-xl dark-surface-alt border p-3 text-center">
                              <p className="text-xs text-slate-400 mb-1">Common band</p>
                              <p className="text-sm font-semibold text-white">{pr.common_band}</p>
                            </div>
                            {pr.median_price > 0 && (
                              <div className="rounded-xl dark-surface-alt border p-3 text-center">
                                <p className="text-xs text-slate-400 mb-1">Median</p>
                                <p className="text-sm font-semibold text-white">~{pr.price_range.currency}{pr.median_price}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {pr.low_vs_high && (
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-1">Lower vs higher prices</h4>
                            <p className="text-sm leading-7 text-slate-200/90">{pr.low_vs_high}</p>
                          </div>
                        )}
                        {pr.pricing_signals?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-1">Pricing signals</h4>
                            <ul className="list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-0.5">
                              {pr.pricing_signals.map((s: string, i: number) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {pr.source_urls?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-white mb-1">Sources</h4>
                            <ul className="space-y-1">
                              {pr.source_urls.map((u: string, i: number) => (
                                <li key={i}>
                                  <a href={u} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-teal-400">
                                    {(() => { try { return new URL(u).hostname.replace("www.", ""); } catch { return u; } })()}
                                    <ExternalLink className="h-3 w-3 shrink-0" />
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <p className="text-xs text-slate-400 italic">
                          {pr.disclaimer || "Retail prices are a public web snapshot at the time of analysis and may change. Always verify on the seller's site before setting final pricing."}
                        </p>
                      </div>
                    </CollapsibleContent>
                  </CollapsibleCard>
                );
              })()}

              {/* ─── Generic Marketing Template Sections ─── */}
              {isGenericTemplate && pack && (
                <>
                  {typeof pack.buyer_experience_ratio === "number" && pack.buyer_experience_ratio < 60 && (
                    <Alert className="dark-surface-alt border rounded-xl">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Low buyer-experience evidence</AlertTitle>
                      <AlertDescription className="text-slate-400">
                        Evidence may be informational rather than buyer experience — refine keywords or context.
                      </AlertDescription>
                    </Alert>
                  )}

                  {pack.positioning_statement && (
                    <CollapsibleCard id="positioning" isOpen={expandedSections.has("positioning")} onToggle={() => toggleSection("positioning")} searchHighlight={isHighlighted("Positioning")}>
                      <SectionHeader title="Positioning" getText={() => pack.positioning_statement} isOpen={expandedSections.has("positioning")} onToggle={() => toggleSection("positioning")} />
                      <CollapsibleContent>
                        <p className="mt-4 text-sm leading-7 text-white">{pack.positioning_statement}</p>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.key_pains?.length > 0 && (
                    <CollapsibleCard id="key-pains" isOpen={expandedSections.has("key-pains")} onToggle={() => toggleSection("key-pains")} searchHighlight={isHighlighted("Key Pains")}>
                      <SectionHeader title="Key Pains (Buyer Language)" getText={() => pack.key_pains.join("\n")} isOpen={expandedSections.has("key-pains")} onToggle={() => toggleSection("key-pains")} />
                      <CollapsibleContent>
                        <ul className="mt-4 list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-1">
                          {pack.key_pains.map((p: string, i: number) => <li key={i}>{p}</li>)}
                        </ul>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.top_objections?.length > 0 && (
                    <CollapsibleCard id="top-objections" isOpen={expandedSections.has("top-objections")} onToggle={() => toggleSection("top-objections")} searchHighlight={isHighlighted("Top Objections")}>
                      <SectionHeader title="Top Objections" getText={() => pack.top_objections.map((o: any) => `Objection: ${o.objection}\nRebuttal: ${o.rebuttal}`).join("\n\n")} isOpen={expandedSections.has("top-objections")} onToggle={() => toggleSection("top-objections")} />
                      <CollapsibleContent>
                        <div className="mt-4 space-y-3">
                          {pack.top_objections.map((o: any, i: number) => (
                            <div key={i} className="grid gap-1 rounded-xl dark-surface-alt border p-3 sm:grid-cols-2">
                              <div><span className="text-xs font-medium text-red-400">Objection:</span><p className="text-sm text-white">{o.objection}</p></div>
                              <div><span className="text-xs font-medium text-teal-400">Rebuttal:</span><p className="text-sm text-white">{o.rebuttal}</p></div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.reassurance_angles?.length > 0 && (
                    <CollapsibleCard id="reassurance-angles" isOpen={expandedSections.has("reassurance-angles")} onToggle={() => toggleSection("reassurance-angles")} searchHighlight={isHighlighted("Reassurance Angles")}>
                      <SectionHeader title="Reassurance Angles" getText={() => pack.reassurance_angles.join("\n")} isOpen={expandedSections.has("reassurance-angles")} onToggle={() => toggleSection("reassurance-angles")} />
                      <CollapsibleContent>
                        <ul className="mt-4 list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-1">
                          {pack.reassurance_angles.map((r: string, i: number) => <li key={i}>{r}</li>)}
                        </ul>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.message_pillars?.length > 0 && (
                    <CollapsibleCard id="message-pillars" isOpen={expandedSections.has("message-pillars")} onToggle={() => toggleSection("message-pillars")} searchHighlight={isHighlighted("Message Pillars")}>
                      <SectionHeader title="Message Pillars" getText={() => pack.message_pillars.join("\n")} isOpen={expandedSections.has("message-pillars")} onToggle={() => toggleSection("message-pillars")} />
                      <CollapsibleContent>
                        <ul className="mt-4 list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-1">
                          {pack.message_pillars.map((m: string, i: number) => <li key={i}>{m}</li>)}
                        </ul>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.hooks?.length > 0 && (
                    <CollapsibleCard id="hooks" isOpen={expandedSections.has("hooks")} onToggle={() => toggleSection("hooks")} searchHighlight={isHighlighted("Hooks")}>
                      <SectionHeader title="Hooks" getText={() => pack.hooks.join("\n")} isOpen={expandedSections.has("hooks")} onToggle={() => toggleSection("hooks")} />
                      <CollapsibleContent>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {pack.hooks.map((h: string, i: number) => (
                            <Badge key={i} className="border-white/10 text-slate-400 bg-transparent text-xs">{h}</Badge>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.ad_angles?.length > 0 && (
                    <CollapsibleCard id="ad-angles" isOpen={expandedSections.has("ad-angles")} onToggle={() => toggleSection("ad-angles")} searchHighlight={isHighlighted("Ad Angles")}>
                      <SectionHeader title="Ad Angles" getText={() => pack.ad_angles.map((a: any) => `${a.angle_name}: ${a.description}`).join("\n")} isOpen={expandedSections.has("ad-angles")} onToggle={() => toggleSection("ad-angles")} />
                      <CollapsibleContent>
                        <div className="mt-4 space-y-4">
                          {pack.ad_angles.map((ad: any, i: number) => (
                            <div key={i} className="rounded-xl dark-surface-alt border p-4">
                              <h4 className="mb-1 font-semibold text-white">{ad.angle_name}</h4>
                              <p className="text-sm leading-7 text-slate-200/90">{ad.description}</p>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.faqs?.length > 0 && (
                    <CollapsibleCard id="faq" isOpen={expandedSections.has("faq")} onToggle={() => toggleSection("faq")} searchHighlight={isHighlighted("FAQ")}>
                      <SectionHeader title="FAQ" getText={() => pack.faqs.map((f: any) => `Q: ${f.q}\nA: ${f.a}`).join("\n\n")} isOpen={expandedSections.has("faq")} onToggle={() => toggleSection("faq")} />
                      <CollapsibleContent>
                        <Accordion type="single" collapsible className="mt-4 w-full">
                          {pack.faqs.map((f: any, i: number) => (
                            <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                              <AccordionTrigger className="text-sm text-left text-slate-200">{f.q}</AccordionTrigger>
                              <AccordionContent className="text-sm leading-7 text-slate-200/90">{f.a}</AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {pack.do_dont_claims && (
                    <CollapsibleCard id="do-dont-claims" isOpen={expandedSections.has("do-dont-claims")} onToggle={() => toggleSection("do-dont-claims")} searchHighlight={isHighlighted("Do / Don't Claims")}>
                      <SectionHeader title="Do / Don't Claims" isOpen={expandedSections.has("do-dont-claims")} onToggle={() => toggleSection("do-dont-claims")} getText={() => {
                        const parts: string[] = [];
                        if (pack.do_dont_claims.do?.length) { parts.push("Safe to claim:"); pack.do_dont_claims.do.forEach((c: string) => parts.push(`✓ ${c}`)); }
                        if (pack.do_dont_claims.dont?.length) { parts.push("Avoid claiming:"); pack.do_dont_claims.dont.forEach((c: string) => parts.push(`✗ ${c}`)); }
                        return parts.join("\n");
                      }} />
                      <CollapsibleContent>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <div>
                            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-emerald-400">
                              <CheckCircle2 className="h-4 w-4" /> Safe to claim
                            </h4>
                            <ul className="space-y-1 text-sm leading-7 text-slate-200/90">
                              {(pack.do_dont_claims.do || []).map((c: string, i: number) => (
                                <li key={i} className="flex items-start gap-1.5">
                                  <span className="mt-0.5 text-emerald-400">•</span> {c}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-red-400">
                              <XCircle className="h-4 w-4" /> Avoid claiming
                            </h4>
                            <ul className="space-y-1 text-sm leading-7 text-slate-200/90">
                              {(pack.do_dont_claims.dont || []).map((c: string, i: number) => (
                                <li key={i} className="flex items-start gap-1.5">
                                  <span className="mt-0.5 text-red-400">•</span> {c}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}
                </>
              )}

              {/* ─── Amazon Copy Pack Template Sections ─── */}
              {!isGenericTemplate && (
                <>
                  {/* Amazon pasted insights */}
                  {pack?.amazon_pasted_insights && (
                    <div className="card-surface space-y-4">
                      <h3 className="text-xl font-bold text-white">Amazon low-star themes (from pasted reviews)</h3>
                      {(pack.amazon_pasted_insights.themes || []).slice(0, 5).map((t: any, i: number) => (
                        <div key={i} className="rounded-xl dark-surface-alt border p-4">
                          <h4 className="mb-2 font-semibold text-white">{i + 1}. {t.title}</h4>
                          <ul className="mb-2 list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-0.5">
                            {(t.bullets || []).slice(0, 2).map((b: string, j: number) => <li key={j}>{b}</li>)}
                          </ul>
                          {(t.quotes || []).slice(0, 2).map((q: string, j: number) => (
                            <span key={j} className="inline-block rounded-lg dark-surface-alt px-2 py-0.5 text-xs italic text-slate-400 mr-2 mb-1">
                              <span className="font-medium not-italic">[Pasted review] </span>"{q}"
                            </span>
                          ))}
                        </div>
                      ))}
                      {(pack.amazon_pasted_insights.objections || []).length > 0 && (
                        <div className="mt-4">
                          <h4 className="mb-2 text-sm font-semibold text-white">Top objections buyers express (pasted)</h4>
                          <div className="space-y-3">
                            {(pack.amazon_pasted_insights.objections || []).slice(0, 3).map((o: any, i: number) => (
                              <div key={i} className="grid gap-1 rounded-xl dark-surface-alt border p-3 sm:grid-cols-2">
                                <div><span className="text-xs font-medium text-red-400">Objection:</span><p className="text-sm text-white">{o.objection}</p></div>
                                <div><span className="text-xs font-medium text-teal-400">Reassurance:</span><p className="text-sm text-white">{o.reassurance}</p></div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!reviewEvidenceAvailable && (
                    <Alert variant="destructive" className="dark-surface-alt border rounded-xl">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Review text not accessible</AlertTitle>
                      <AlertDescription className="text-slate-400">Review text not accessible — report based on listing claims.</AlertDescription>
                    </Alert>
                  )}

                  {hasYoutubeWithoutComments && (
                    <Alert className="dark-surface-alt border rounded-xl">
                      <Info className="h-4 w-4" />
                      <AlertTitle>YouTube evidence note</AlertTitle>
                      <AlertDescription className="text-slate-400">YouTube evidence from descriptions/pages (comments not accessible).</AlertDescription>
                    </Alert>
                  )}

                  {/* Pain Themes */}
                  <CollapsibleCard id="themes" isOpen={expandedSections.has("themes")} onToggle={() => toggleSection("themes")} searchHighlight={isHighlighted(themeHeading)}>
                    <SectionHeader title={themeHeading} getText={() => themes.map((t: any, i: number) => themeText(t, i)).join("\n\n")} isOpen={expandedSections.has("themes")} onToggle={() => toggleSection("themes")} />
                    <CollapsibleContent>
                      <div className="mt-4 space-y-4">
                        {themes.map((t: any, i: number) => (
                          <div key={i} className="rounded-xl dark-surface-alt border p-4">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="mb-2 font-semibold text-white">{i + 1}. {t.title}</h4>
                              <CopyButton getText={() => themeText(t, i)} size="icon" variant="ghost" />
                            </div>
                            <ul className="mb-2 list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-0.5">
                              {t.bullets?.map((b: string, j: number) => <li key={j}>{b}</li>)}
                            </ul>
                            {t.snippets?.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {t.snippets.map((s: any, j: number) => {
                                  const text = typeof s === 'string' ? s : s.text;
                                  const label = typeof s === 'object' && s.label ? s.label : null;
                                  return (
                                    <span key={j} className="inline-block rounded-lg dark-surface-alt px-2 py-0.5 text-xs italic text-slate-400">
                                      {label && <span className="font-medium not-italic">[{label}] </span>}"{text}"
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {t.messaging_angle && (
                              <div className="mt-2 rounded-lg bg-teal-500/15 px-3 py-2 text-xs text-teal-300">
                                <Popover>
                                  <span className="font-medium inline-flex items-center gap-1">Messaging angle:
                                    <PopoverTrigger asChild>
                                      <button type="button" className="inline-flex"><HelpCircle className="h-3 w-3 text-teal-400/70 cursor-help" /></button>
                                    </PopoverTrigger>
                                  </span>
                                  <PopoverContent className="max-w-xs text-xs space-y-1 dark-surface border" side="top">
                                    <p className="font-semibold text-slate-200 mb-1">What's a messaging angle?</p>
                                    <ul className="list-disc pl-4 space-y-0.5 text-slate-300">
                                      <li>A short suggestion for how to frame your copy</li>
                                      <li>What to emphasise to the buyer</li>
                                      <li>What to reassure them about</li>
                                      <li>What proof or evidence to reference</li>
                                      <li>Use it as a starting point for headlines or bullets</li>
                                    </ul>
                                  </PopoverContent>
                                </Popover>{" "}{t.messaging_angle}
                              </div>
                            )}
                            {t.evidence_strength && (
                              <p className="mt-1 text-[11px] text-slate-400 italic">
                                Evidence strength: {t.evidence_strength}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </CollapsibleCard>

                  {/* Seller Claims */}
                  {sellerClaims.length > 0 && (
                    <CollapsibleCard id="seller-claims" isOpen={expandedSections.has("seller-claims")} onToggle={() => toggleSection("seller-claims")} searchHighlight={isHighlighted("Seller Claims")}>
                      <SectionHeader title="Seller Claims Spotted on Listings" getText={() => sellerClaims.join("\n")} isOpen={expandedSections.has("seller-claims")} onToggle={() => toggleSection("seller-claims")} />
                      <CollapsibleContent>
                        <p className="mt-4 text-xs text-slate-400 mb-3">Common marketing phrases found in competitor listing copy — not customer quotes.</p>
                        <ul className="list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-1">
                          {sellerClaims.map((claim, i) => <li key={i}>{claim}</li>)}
                        </ul>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}

                  {/* Desired Outcomes */}
                  <CollapsibleCard id="desired-outcomes" isOpen={expandedSections.has("desired-outcomes")} onToggle={() => toggleSection("desired-outcomes")} searchHighlight={isHighlighted("Desired Outcomes")}>
                    <SectionHeader title="Desired Outcomes" getText={() => outcomes.join("\n")} isOpen={expandedSections.has("desired-outcomes")} onToggle={() => toggleSection("desired-outcomes")} />
                    <CollapsibleContent>
                      <ul className="mt-4 list-disc pl-5 text-sm leading-7 text-slate-200/90 space-y-1">
                        {outcomes.map((o, i) => <li key={i}>{o}</li>)}
                      </ul>
                    </CollapsibleContent>
                  </CollapsibleCard>

                  {/* Objections */}
                  <CollapsibleCard id="objections" isOpen={expandedSections.has("objections")} onToggle={() => toggleSection("objections")} searchHighlight={isHighlighted("Objections")}>
                    <SectionHeader title="Objections & Reassurance" getText={() => objections.map((o: any) => `Objection: ${o.objection}\nReassurance: ${o.reassurance}`).join("\n\n")} isOpen={expandedSections.has("objections")} onToggle={() => toggleSection("objections")} />
                    <CollapsibleContent>
                      {!reviewEvidenceAvailable && (
                        <p className="mt-2 text-xs text-slate-400">Based on listing claims (not verified customer quotes)</p>
                      )}
                      <div className="mt-4 space-y-3">
                        {objections.map((o: any, i: number) => (
                          <div key={i} className="grid gap-1 rounded-xl dark-surface-alt border p-3 sm:grid-cols-2">
                            <div><span className="text-xs font-medium text-red-400">Objection:</span><p className="text-sm text-white">{o.objection}</p></div>
                            <div><span className="text-xs font-medium text-teal-400">Reassurance:</span><p className="text-sm text-white">{o.reassurance}</p></div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </CollapsibleCard>

                  {/* Copy Pack */}
                  {pack && (
                    <CollapsibleCard id="copy-pack" isOpen={expandedSections.has("copy-pack")} onToggle={() => toggleSection("copy-pack")} searchHighlight={isHighlighted("Copy Pack")}>
                      <SectionHeader title="Conversion Copy Pack" isOpen={expandedSections.has("copy-pack")} onToggle={() => toggleSection("copy-pack")} getText={() => {
                        const parts: string[] = [];
                        if (pack.titles?.length) { parts.push("Titles:"); pack.titles.forEach((t: string, i: number) => parts.push(`${i+1}. ${t}`)); parts.push(""); }
                        if (pack.bullets?.length) { parts.push("Bullets:"); pack.bullets.forEach((b: string) => parts.push(`- ${b}`)); parts.push(""); }
                        if (pack.description) { parts.push("Description:"); parts.push(pack.description); parts.push(""); }
                        if (pack.faqs?.length) { parts.push("FAQs:"); pack.faqs.forEach((f: any) => { parts.push(`Q: ${f.q}`); parts.push(`A: ${f.a}`); parts.push(""); }); }
                        if (pack.ad_hooks?.length) { parts.push("Ad Hooks:"); pack.ad_hooks.forEach((h: string) => parts.push(`- ${h}`)); }
                        return parts.join("\n");
                      }} />
                      <CollapsibleContent>
                        <div className="mt-4 space-y-6">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Title Options</h4>
                              <CopyButton getText={() => (pack.titles || []).map((t: string, i: number) => `${i+1}. ${t}`).join("\n")} size="icon" variant="ghost" />
                            </div>
                            {(pack.titles?.length || 0) >= 6 ? (
                              <div className="space-y-3">
                                <div>
                                  <p className="text-xs text-teal-400 font-medium mb-1">Benefit-led</p>
                                  <ol className="list-decimal pl-5 text-sm space-y-1 text-white">
                                    {pack.titles.slice(0, 2).map((t: string, i: number) => <li key={i}>{t}</li>)}
                                  </ol>
                                </div>
                                <div>
                                  <p className="text-xs text-amber-400 font-medium mb-1">Problem-led</p>
                                  <ol className="list-decimal pl-5 text-sm space-y-1 text-white" start={3}>
                                    {pack.titles.slice(2, 4).map((t: string, i: number) => <li key={i}>{t}</li>)}
                                  </ol>
                                </div>
                                <div>
                                  <p className="text-xs text-blue-400 font-medium mb-1">Trust-led</p>
                                  <ol className="list-decimal pl-5 text-sm space-y-1 text-white" start={5}>
                                    {pack.titles.slice(4, 6).map((t: string, i: number) => <li key={i}>{t}</li>)}
                                  </ol>
                                </div>
                              </div>
                            ) : (
                              <ol className="list-decimal pl-5 text-sm space-y-1 text-white">
                                {pack.titles?.map((t: string, i: number) => <li key={i}>{t}</li>)}
                              </ol>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Bullets</h4>
                              <CopyButton getText={() => (pack.bullets || []).join("\n")} size="icon" variant="ghost" />
                            </div>
                            <ul className="text-sm space-y-1 text-white">
                              {pack.bullets?.map((b: string, i: number) => <li key={i}>{b}</li>)}
                            </ul>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Description</h4>
                              <CopyButton getText={() => pack.description || ""} size="icon" variant="ghost" />
                            </div>
                            <p className="text-sm leading-7 text-slate-200/90">{pack.description}</p>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">FAQs</h4>
                              <CopyButton getText={() => (pack.faqs || []).map((f: any) => `Q: ${f.q}\nA: ${f.a}`).join("\n\n")} size="icon" variant="ghost" />
                            </div>
                            <div className="space-y-2">
                              {pack.faqs?.map((f: any, i: number) => (
                                <div key={i} className="rounded-xl dark-surface-alt p-3">
                                  <p className="text-sm font-medium text-white">{f.q}</p>
                                  <p className="text-sm leading-7 text-slate-200/90">{f.a}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Headlines (3 options)</h4>
                              <CopyButton getText={() => (pack.ad_hooks || []).join("\n")} size="icon" variant="ghost" />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {pack.ad_hooks?.map((h: string, i: number) => (
                                <Badge key={i} className="border-white/10 text-slate-400 bg-transparent text-xs">{h}</Badge>
                              ))}
                            </div>
                          </div>
                          {pack.copy_angles?.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-white">Copy Angles</h4>
                                <CopyButton getText={() => (pack.copy_angles || []).join("\n\n")} size="icon" variant="ghost" />
                              </div>
                              <p className="text-xs text-slate-400 mb-2">Problem → Proof → Promise (one sentence each)</p>
                              <ol className="list-decimal pl-5 text-sm space-y-2 text-white">
                                {pack.copy_angles.map((c: string, i: number) => <li key={i}>{c}</li>)}
                              </ol>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </CollapsibleCard>
                  )}
                </>
              )}

              {/* Sales Page Builder */}
              {(pack || themes.length > 0) && (() => {
                const salesPage = buildSalesPage(report, themes, objections, outcomes, sellerClaims, pack);
                return (
                  <CollapsibleCard id="sales-page" isOpen={expandedSections.has("sales-page")} onToggle={() => toggleSection("sales-page")} searchHighlight={isHighlighted("Sales Page Builder")}>
                    <SectionHeader title="Sales Page Builder (Shopify-ready)" getText={() => buildSalesPageText(salesPage)} isOpen={expandedSections.has("sales-page")} onToggle={() => toggleSection("sales-page")} />
                    {isFree ? (
                      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center space-y-3">
                        <Lock className="h-8 w-8 text-slate-500 mx-auto" />
                        <p className="text-sm font-medium text-slate-300">Sales Page Builder</p>
                        <p className="text-xs text-slate-400 max-w-sm mx-auto">Turn your report into a Shopify-ready sales page in 1 click—headlines, FAQs, offer stack, and CTAs.</p>
                        <Button asChild size="sm" className="btn-gradient"><Link to="/pricing" onClick={() => phCapture("upgrade_clicked", { source: "report" })}>Upgrade to Pro</Link></Button>
                      </div>
                    ) : (
                    <CollapsibleContent>
                      <div className="mt-4 space-y-6">

                        {/* Hero Headlines */}
                        {salesPage.heroHeadlines.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Hero Headlines</h4>
                              <CopyButton getText={() => salesPage.heroHeadlines.map((h, i) => `${i+1}. ${h}`).join("\n") + (salesPage.heroSubhead ? `\n\nSubhead: ${salesPage.heroSubhead}` : "")} size="icon" variant="ghost" />
                            </div>
                            <ol className="list-decimal pl-5 text-sm space-y-1 text-white">
                              {salesPage.heroHeadlines.map((h, i) => <li key={i}>{h}</li>)}
                            </ol>
                            {salesPage.heroSubhead && (
                              <p className="mt-2 text-xs text-slate-400">Subhead: <span className="text-slate-200">{salesPage.heroSubhead}</span></p>
                            )}
                          </div>
                        )}

                        {/* Benefit Bullets */}
                        {salesPage.benefitBullets.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Benefit Bullets</h4>
                              <CopyButton getText={() => salesPage.benefitBullets.map(b => `• ${b}`).join("\n")} size="icon" variant="ghost" />
                            </div>
                            <ul className="text-sm space-y-1 text-white">
                              {salesPage.benefitBullets.map((b, i) => <li key={i}>• {b}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Why This / Why Now */}
                        {salesPage.whyThisWhyNow && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Why This / Why Now</h4>
                              <CopyButton getText={() => salesPage.whyThisWhyNow} size="icon" variant="ghost" />
                            </div>
                            <p className="text-sm leading-7 text-slate-200/90 whitespace-pre-line">{salesPage.whyThisWhyNow}</p>
                          </div>
                        )}

                        {/* How It Works */}
                        {salesPage.howItWorks.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">How It Works</h4>
                              <CopyButton getText={() => salesPage.howItWorks.join("\n")} size="icon" variant="ghost" />
                            </div>
                            <ol className="text-sm space-y-1 text-white pl-2">
                              {salesPage.howItWorks.map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                          </div>
                        )}

                        {/* Social Proof */}
                        {salesPage.socialProof.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Social Proof</h4>
                              <CopyButton getText={() => salesPage.socialProof.join("\n\n")} size="icon" variant="ghost" />
                            </div>
                            <div className="space-y-2">
                              {salesPage.socialProof.map((q, i) => (
                                <div key={i} className="rounded-xl dark-surface-alt p-3">
                                  <p className="text-sm italic text-slate-200/90">{q}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* FAQ */}
                        {salesPage.faq.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">FAQ</h4>
                              <CopyButton getText={() => salesPage.faq.map(f => `Q: ${f.q}\nA: ${f.a}`).join("\n\n")} size="icon" variant="ghost" />
                            </div>
                            <div className="space-y-2">
                              {salesPage.faq.map((f, i) => (
                                <div key={i} className="rounded-xl dark-surface-alt p-3">
                                  <p className="text-sm font-medium text-white">{f.q}</p>
                                  <p className="text-sm leading-7 text-slate-200/90">{f.a}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Offer Stack + Guarantee */}
                        {salesPage.offerStack.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">Offer Stack</h4>
                              <CopyButton getText={() => salesPage.offerStack.join("\n") + `\n\n${salesPage.guaranteeSuggestion}`} size="icon" variant="ghost" />
                            </div>
                            <ul className="text-sm space-y-1 text-white">
                              {salesPage.offerStack.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                            <p className="mt-3 text-xs text-teal-300/80 italic">{salesPage.guaranteeSuggestion}</p>
                          </div>
                        )}

                        {/* CTA Options */}
                        {salesPage.ctaOptions.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-white">CTA Options</h4>
                              <CopyButton getText={() => salesPage.ctaOptions.map((c, i) => `${i+1}. ${c}`).join("\n")} size="icon" variant="ghost" />
                            </div>
                            <ol className="list-decimal pl-5 text-sm space-y-1 text-white">
                              {salesPage.ctaOptions.map((c, i) => <li key={i}>{c}</li>)}
                            </ol>
                          </div>
                        )}

                        {/* Who It's For / Not For */}
                        <div className="grid gap-4 md:grid-cols-2">
                          {salesPage.whoItsFor.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-white flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-teal-400" /> Who It's For</h4>
                                <CopyButton getText={() => salesPage.whoItsFor.join("\n")} size="icon" variant="ghost" />
                              </div>
                              <ul className="text-sm space-y-1 text-slate-200/90">
                                {salesPage.whoItsFor.map((w, i) => <li key={i}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                          {salesPage.whoItsNotFor.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-white flex items-center gap-1.5"><XCircle className="h-3.5 w-3.5 text-red-400" /> Who It's Not For</h4>
                                <CopyButton getText={() => salesPage.whoItsNotFor.join("\n")} size="icon" variant="ghost" />
                              </div>
                              <ul className="text-sm space-y-1 text-slate-200/90">
                                {salesPage.whoItsNotFor.map((w, i) => <li key={i}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* Disclaimer */}
                        {salesPage.disclaimer && (
                          <Alert className="border-amber-500/30 bg-amber-500/5">
                            <AlertTriangle className="h-4 w-4 text-amber-400" />
                            <AlertTitle className="text-amber-300 text-sm">Disclaimer</AlertTitle>
                            <AlertDescription className="text-slate-300 text-xs">{salesPage.disclaimer}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </CollapsibleContent>
                    )}
                  </CollapsibleCard>
                );
              })()}

              {/* ── Improve My Listing / Page ── */}
              <CollapsibleCard id="improve-listing" isOpen={expandedSections.has("improve-listing")} onToggle={() => toggleSection("improve-listing")} searchHighlight={isHighlighted("Improve My Listing")}>
                <SectionHeader
                  title="Improve My Listing / Page"
                  getText={() => listingResult ? buildImprovementText(listingResult) : ""}
                  isOpen={expandedSections.has("improve-listing")}
                  onToggle={() => toggleSection("improve-listing")}
                />
                {isFree ? (
                  <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center space-y-3">
                    <Lock className="h-8 w-8 text-slate-500 mx-auto" />
                    <p className="text-sm font-medium text-slate-300">Improve My Listing / Page</p>
                    <p className="text-xs text-slate-400 max-w-sm mx-auto">Paste your listing and get a clearer, higher-converting rewrite + compliance notes.</p>
                    <Button asChild size="sm" className="btn-gradient"><Link to="/pricing" onClick={() => phCapture("upgrade_clicked", { source: "report" })}>Upgrade to Pro</Link></Button>
                  </div>
                ) : (
                <CollapsibleContent>
                  <div className="mt-4 space-y-4">
                    <Textarea
                      placeholder="Paste your current Amazon listing or Shopify description here…"
                      className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                      value={listingInput}
                      onChange={e => setListingInput(e.target.value)}
                    />
                    <Button
                      className="btn-gradient"
                      size="sm"
                      disabled={listingInput.trim().length < 20}
                      onClick={() => {
                        const result = improveListing(listingInput, themes, objections, outcomes, sellerClaims, pack, report.industry_context);
                        setListingResult(result);
                      }}
                    >
                      <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Improve My Listing
                    </Button>

                    {listingResult && (
                      <div className="space-y-6 pt-2">
                        {/* Copy Everything */}
                        <div className="flex justify-end">
                          <CopyButton getText={() => buildImprovementText(listingResult)} label="Copy everything" />
                        </div>

                        {/* Improved Same Length */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-white">Improved Copy (Same Length)</h4>
                            <CopyButton getText={() => listingResult.improvedSameLength} size="icon" variant="ghost" />
                          </div>
                          <pre className="whitespace-pre-wrap text-sm text-slate-200/90 bg-white/5 rounded-lg p-4 border border-white/5">{listingResult.improvedSameLength}</pre>
                        </div>

                        {/* Improved Longer */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-white">Improved Copy (Longer Version)</h4>
                            <CopyButton getText={() => listingResult.improvedLonger} size="icon" variant="ghost" />
                          </div>
                          <pre className="whitespace-pre-wrap text-sm text-slate-200/90 bg-white/5 rounded-lg p-4 border border-white/5">{listingResult.improvedLonger}</pre>
                        </div>

                        {/* Bullet Improvements */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-white">Bullet Improvements</h4>
                            <CopyButton
                              getText={() => listingResult.bulletImprovements.map((b, i) => `${i+1}. Original: ${b.original}\n   Improved: ${b.improved}\n   Reason: ${b.reason}`).join("\n\n")}
                              size="icon" variant="ghost"
                            />
                          </div>
                          <div className="space-y-3">
                            {listingResult.bulletImprovements.map((b, i) => (
                              <div key={i} className="rounded-lg border border-white/5 bg-white/[0.02] p-3 space-y-1.5">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs font-bold text-slate-500 mt-0.5 shrink-0">{i+1}</span>
                                  <div className="flex-1 min-w-0 space-y-1">
                                    <p className="text-xs text-slate-400 line-through">{b.original}</p>
                                    <p className="text-sm text-white">{b.improved}</p>
                                    <p className="text-xs text-slate-400 italic">{b.reason}</p>
                                  </div>
                                </div>
                                <Badge className="bg-teal-500/15 text-teal-300 border-0 text-[10px]">{b.source}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Do Not Claim */}
                        <Alert className="border-amber-500/30 bg-amber-500/5">
                          <AlertTriangle className="h-4 w-4 text-amber-400" />
                          <AlertTitle className="text-amber-300 text-sm">Do Not Claim — Compliance Notes</AlertTitle>
                          <AlertDescription>
                            <ul className="mt-1 space-y-1">
                              {listingResult.doNotClaim.map((c, i) => (
                                <li key={i} className="text-xs text-slate-300">• {c}</li>
                              ))}
                            </ul>
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
                )}
              </CollapsibleCard>

              {/* Sources */}
              <CollapsibleCard id="sources" isOpen={expandedSections.has("sources")} onToggle={() => toggleSection("sources")} searchHighlight={isHighlighted("Sources")}>
                <SectionHeader title={evidenceSectionTitle} getText={() => sources.join("\n")} isOpen={expandedSections.has("sources")} onToggle={() => toggleSection("sources")} />
                <CollapsibleContent>
                  <ul className="mt-4 space-y-2">
                    {sources.map((s, i) => {
                      const label = getSourceLabel(s);
                      const { type: sourceType, category } = getSourceType(s);
                      return (
                        <li key={i} className="flex items-center gap-2">
                          {sourceType && (
                            <Badge className="border-white/10 text-slate-400 bg-transparent text-[10px] shrink-0">{sourceType}</Badge>
                          )}
                          {category && (
                            <Badge className={`text-[10px] shrink-0 border-0 ${category === "user_voice" ? "bg-teal-500/15 text-teal-300" : "bg-blue-500/15 text-blue-300"}`}>
                              {category === "user_voice"
                                ? (evidenceSourcesUsed.find((e: any) => e.url === s)?.comments_extracted === false
                                  ? "User voice (limited extraction)"
                                  : "User voice")
                                : "Editorial"}
                            </Badge>
                          )}
                          <a href={s} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-teal-400">
                            {label}
                            <ExternalLink className="h-3 w-3 shrink-0" />
                          </a>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyLink(s, i)}>
                            {copiedIdx === i ? <Check className="h-3 w-3 text-teal-400" /> : <Copy className="h-3 w-3 text-slate-400" />}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </CollapsibleContent>
              </CollapsibleCard>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline" className="border-white/10 text-slate-400 hover:text-white"><Link to="/history"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to History</Link></Button>
                <Link to="/generate" className="btn-gradient inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition"><PlusCircle className="h-4 w-4" /> Generate Another</Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout></ReportErrorBoundary></RequireAuth>
  );
};

export default Report;
